import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { User } from "@supabase/supabase-js";
import { AUTH_USER_MISSING_ERROR_CODE } from "@/lib/db";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

function getDaysInMonth(year: number, month: number): number {
  if (month === 2) {
    return (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) ? 29 : 28;
  }
  return [31, 0, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
}

/** Hyphen/dash placeholders that must be treated as empty. OAuth can return these when no name. */
const PLACEHOLDER_STRINGS = ["-", "—", "–", "\u2010", "\u2011", "\u2012", "\u2013", "\u2014", "\u2015"];

function isPlaceholder(s: string | undefined): boolean {
  if (!s || typeof s !== "string") return true;
  const t = s.trim();
  return !t || PLACEHOLDER_STRINGS.includes(t);
}

/** Format full_name from Google Auth to username with underscores (e.g. "John Doe" -> "John_Doe"). Returns "" when empty, invalid, or placeholder (e.g. "-"). */
function fullNameToUsername(fullName: string | undefined): string {
  if (!fullName || typeof fullName !== "string") return "";
  if (isPlaceholder(fullName)) return "";
  const out = fullName
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "");
  return out || "";
}

interface CompleteProfileModalProps {
  user: User;
  onSubmit: (data: { username: string; birthDate: string; isTeacher: boolean }) => Promise<void>;
}

/** Derive username from OAuth metadata. Uses full_name first, then name. Spaces become underscores. Returns "" when nothing valid (including "-" placeholders). */
function deriveUsernameFromMetadata(user: User | undefined): string {
  const fullName = user?.user_metadata?.full_name as string | undefined;
  const name = user?.user_metadata?.name as string | undefined;
  const raw = fullName ?? name ?? "";
  if (!raw || isPlaceholder(raw)) return "";
  return fullNameToUsername(raw) || "";
}

export default function CompleteProfileModal({ user, onSubmit }: CompleteProfileModalProps) {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [day, setDay] = useState<number | "">("");
  const [month, setMonth] = useState<number | "">("");
  const [year, setYear] = useState<number | "">("");
  const [isTeacher, setIsTeacher] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Pre-fill username from OAuth metadata when user (or metadata) loads. Watches user to fix race where metadata arrives after mount.
  useEffect(() => {
    console.log("DEBUG: Current metadata name:", user?.user_metadata?.full_name);
    const googleName = user?.user_metadata?.full_name || user?.user_metadata?.name || "";
    const formattedName = googleName.replace(/\s+/g, "_");
    if (formattedName && !isPlaceholder(formattedName) && !username) setUsername(formattedName);
  }, [user, username]);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const daysInSelectedMonth = (year && month)
    ? getDaysInMonth(year, month)
    : 31;
  const days = Array.from({ length: daysInSelectedMonth }, (_, i) => i + 1);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedUsername = (username ?? "").trim();
    if (!trimmedUsername || isPlaceholder(trimmedUsername)) {
      setError(t("completeProfile.errors.usernameRequired"));
      return;
    }

    if (!day || !month || !year) {
      setError(t("completeProfile.errors.dateRequired"));
      return;
    }

    const birthDate = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    setSubmitting(true);

    try {
      await onSubmit({ username: trimmedUsername, birthDate, isTeacher });
      // Success: AuthContext updates profile (optimistic + refreshProfile), modal unmounts via guard
    } catch (err: unknown) {
      const fallback = t("completeProfile.errors.submitFailed");
      let displayMessage = fallback;

      if (err && typeof err === "object") {
        const e = err as { message?: string; details?: string; hint?: string; code?: string };
        if (e.code === AUTH_USER_MISSING_ERROR_CODE) {
          displayMessage = t("completeProfile.errors.accountRemoved");
        } else {
          console.error("[CompleteProfileModal] Save failed:", e);
          const parts: string[] = [];
          if (e.message) parts.push(e.message);
          if (e.code) parts.push(`Code: ${e.code}`);
          if (e.details) parts.push(`Details: ${e.details}`);
          if (e.hint) parts.push(`Hint: ${e.hint}`);
          if (parts.length > 0) displayMessage = parts.join(" ");
        }
      } else {
        console.error("[CompleteProfileModal] Save failed (non-object error):", err);
      }

      setError(displayMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 300,
        padding: 16,
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        style={{
          background: "hsl(var(--bg2))",
          border: "1px solid hsl(var(--border))",
          borderRadius: "var(--radius)",
          padding: 20,
          width: "100%",
          maxWidth: 440,
          maxHeight: "90dvh",
          overflowY: "auto",
          overflowX: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,.5)",
        }}
      >
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              fontFamily: "Instrument Serif, serif",
              fontSize: 22,
              fontWeight: 600,
              color: "hsl(var(--foreground))",
            }}
          >
            {t("completeProfile.title")}
          </div>
          <div
            style={{
              fontSize: 14,
              color: "hsl(var(--muted-foreground))",
              marginTop: 4,
            }}
          >
            {t("completeProfile.subtitle")}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Username */}
          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor="complete-profile-username"
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 500,
                color: "hsl(var(--foreground))",
                marginBottom: 6,
              }}
            >
              {t("completeProfile.username")}
            </label>
            <input
              id="complete-profile-username"
              type="text"
              value={isPlaceholder(username) ? "" : (username ?? "")}
              onChange={(e) => {
                const v = e.target.value;
                setUsername(isPlaceholder(v) ? "" : v);
              }}
              placeholder={t("completeProfile.usernamePlaceholder")}
              autoComplete="username"
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "var(--radius-sm)",
                border: "1px solid hsl(var(--border))",
                background: "hsl(var(--bg))",
                color: "hsl(var(--foreground))",
                fontFamily: "DM Sans, sans-serif",
                fontSize: 14,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Date of Birth - 3 dropdowns */}
          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 500,
                color: "hsl(var(--foreground))",
                marginBottom: 6,
              }}
            >
              {t("completeProfile.dateOfBirth")}
            </label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 2fr 1fr",
                gap: 8,
              }}
            >
              <select
                value={day || ""}
                onChange={(e) => setDay(e.target.value ? parseInt(e.target.value, 10) : "")}
                style={{
                  padding: "10px 12px",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid hsl(var(--border))",
                  background: "hsl(var(--bg))",
                  color: "hsl(var(--foreground))",
                  fontFamily: "DM Sans, sans-serif",
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                <option value="">{t("completeProfile.day")}</option>
                {days.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <select
                value={month || ""}
                onChange={(e) => {
                  const v = e.target.value ? parseInt(e.target.value, 10) : "";
                  setMonth(v);
                  if (day && year && v) {
                    const maxDay = getDaysInMonth(year, v);
                    if (day > maxDay) setDay(maxDay);
                  }
                }}
                style={{
                  padding: "10px 12px",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid hsl(var(--border))",
                  background: "hsl(var(--bg))",
                  color: "hsl(var(--foreground))",
                  fontFamily: "DM Sans, sans-serif",
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                <option value="">{t("completeProfile.month")}</option>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>{t(`completeProfile.month_${i + 1}`)}</option>
                ))}
              </select>
              <select
                value={year || ""}
                onChange={(e) => {
                  const v = e.target.value ? parseInt(e.target.value, 10) : "";
                  setYear(v);
                  if (day && month && v) {
                    const maxDay = getDaysInMonth(v, month);
                    if (day > maxDay) setDay(maxDay);
                  }
                }}
                style={{
                  padding: "10px 12px",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid hsl(var(--border))",
                  background: "hsl(var(--bg))",
                  color: "hsl(var(--foreground))",
                  fontFamily: "DM Sans, sans-serif",
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                <option value="">{t("completeProfile.year")}</option>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Teacher checkbox - prominent block with soft background */}
          <div
            style={{
              marginBottom: 24,
              padding: 16,
              borderRadius: "var(--radius-sm)",
              background: "linear-gradient(135deg, rgba(0,229,160,.08), rgba(109,90,255,.08))",
              border: "1px solid rgba(109,90,255,.2)",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                cursor: "pointer",
                color: "hsl(var(--foreground))",
                fontFamily: "DM Sans, sans-serif",
                fontSize: 15,
                fontWeight: 500,
              }}
            >
              <input
                type="checkbox"
                checked={isTeacher}
                onChange={(e) => setIsTeacher(e.target.checked)}
                style={{
                  width: 20,
                  height: 20,
                  accentColor: "hsl(var(--primary))",
                  cursor: "pointer",
                }}
              />
              <span>{t("completeProfile.iAmTeacher")}</span>
            </label>
          </div>

          {error && (
            <div
              style={{
                marginBottom: 16,
                padding: "10px 12px",
                borderRadius: "var(--radius-sm)",
                background: "rgba(255,90,138,.1)",
                color: "hsl(var(--accent3))",
                fontSize: 13,
                wordBreak: "break-word",
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: "100%",
              minHeight: 44,
              padding: "12px 20px",
              borderRadius: "var(--radius-sm)",
              border: "none",
              background: submitting
                ? "hsl(var(--muted-foreground))"
                : "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent2)))",
              color: "white",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 15,
              fontWeight: 600,
              cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting ? 0.7 : 1,
              transition: "opacity .2s",
            }}
          >
            {submitting ? t("completeProfile.submitting") : t("completeProfile.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
